import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import mixpanel from 'mixpanel-browser';
import { environment } from '@env/environment';
import { isMixpanelEnabled } from '../../analytics/mixpanel-bootstrap';
import type { UserEnvStored } from './user-env/user-env.service';

export type AnalyticsPropertyValue = string | number | boolean;
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly enabled = isMixpanelEnabled(environment);

  track(event: string, properties?: AnalyticsProperties): void {
    if (!this.enabled) {
      return;
    }
    mixpanel.track(event, properties);
  }

  trackPageView(url: string, properties?: AnalyticsProperties): void {
    if (!this.enabled) {
      return;
    }
    mixpanel.track_pageview({ page: url, ...properties });
  }

  identifyUser(keycloak: Keycloak, userEnv: UserEnvStored): void {
    if (!this.enabled) {
      return;
    }

    const userId = this.resolveUserId(keycloak);
    if (!userId) {
      return;
    }

    const superProperties = this.buildSuperProperties(keycloak, userEnv);
    mixpanel.identify(userId);
    mixpanel.register(superProperties);
    mixpanel.people.set({
      $name: userEnv.full_name ?? superProperties['username'],
      company_id: superProperties['company_id'],
      company_name: superProperties['company_name'],
      primary_role_type: superProperties['primary_role_type'],
      branch_id: superProperties['branch_id'],
      language: superProperties['language'],
    });
  }

  reset(): void {
    if (!this.enabled) {
      return;
    }
    mixpanel.reset();
  }

  private resolveUserId(keycloak: Keycloak): string | null {
    const tokenParsed = keycloak.tokenParsed;
    if (!tokenParsed) {
      return null;
    }
    const sub = tokenParsed['sub'];
    if (typeof sub === 'string' && sub.trim()) {
      return sub;
    }
    const username = tokenParsed['preferred_username'];
    if (typeof username === 'string' && username.trim()) {
      return username;
    }
    return null;
  }

  private buildSuperProperties(keycloak: Keycloak, userEnv: UserEnvStored): AnalyticsProperties {
    const tokenParsed = keycloak.tokenParsed;
    const username =
      typeof tokenParsed?.['preferred_username'] === 'string' ? tokenParsed['preferred_username'] : '';
    const realm = typeof localStorage !== 'undefined' ? (localStorage.getItem('realm') ?? '') : '';

    const properties: AnalyticsProperties = {
      app_version: environment.appVersion,
      realm,
      username,
    };

    const companyId = userEnv.company?.id;
    if (companyId != null && companyId !== '') {
      properties['company_id'] = String(companyId);
    }

    const companyName = userEnv.company?.description;
    if (companyName) {
      properties['company_name'] = companyName;
    }

    const workingPeriodId = userEnv.working_period?.id;
    if (workingPeriodId != null && workingPeriodId !== '') {
      properties['working_period_id'] = String(workingPeriodId);
    }

    if (userEnv.language) {
      properties['language'] = userEnv.language;
    }

    if (userEnv.primary_role_type) {
      properties['primary_role_type'] = userEnv.primary_role_type;
    }

    const branchId = userEnv.branch?.id;
    if (branchId != null && branchId !== '') {
      properties['branch_id'] = String(branchId);
    }

    return properties;
  }
}
